import { CommonModule } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { IUser, IUserEx, SimpleUser } from '@vierkant-software/types__api';
import { SkeletonModule } from 'primeng/skeleton';
import { Globals } from 'src/app/services/globals.service';
import { UserSyncService } from 'src/app/services/userSync.service';
import { Format, formatUser } from 'src/util/formatting/user';
import { VAvatarComponent } from '../v-avatar/v-avatar.component';

// TODO: replace all instances in frontend with IUserEx or preferably **SimpleUser**
/**
 * Display the user.
 * May include the user's profile image, or a colorized fallback.
 *
 * @name    User
 * @tags    Data, Userr
 */
@Component({
    selector:    'v-user',
    styleUrls:   ['./v-user.component.sass'],
    templateUrl: './v-user.component.haml',
    imports:     [CommonModule, VAvatarComponent, SkeletonModule],
    standalone:  true
})
export class VUserComponent{

    private readonly userService = inject(UserSyncService);

    /**
     * The user to display.
     * May either be the user's data, or the user's ID (UUID).
     * If an ID is provided, the user is resolved via the `userSync` service
     * and a skeleton is displayed while the data are loading.
     *
     * @values  SimpleUser | IUserEx | IUser | string
     * @example
     * ```
     * %v-user{[value]: "demo.user"}
     * ```
     * @example ID
     * ```
     * %v-user{[value]: "demo.user.ID"}
     * ```
     */
    public value = input<SimpleUser | IUserEx | IUser | string>();

    /**
     * Format of the user's name.
     *
     * @values  "normal" | "initial" | "none" | {firstname: Format, lastname: Format}
     * @default "normal"
     * @example Initial
     * ```
     * %v-user{[value]: "demo.user", format: "initial"}
     * ```
     * @example Object
     * ```
     * %v-user{[value]: "demo.user", [format]: "{firstname: 'normal', lastname: 'none'}"}
     * ```
     */
    public format = input<Format>("normal");

    /**
     * Profile picture, if applicable.
     * If the user is provided as an ID, the resolved user's picture is used as fallback.
     *
     * @values  string | SafeUrl
     * @example
     * ```
     * %v-user{[value]: "demo.user", [image]: "demo.user_image"}
     * ```
     */
    public image = input<string | SafeUrl>();

    /**
     * Show a fallback image if the user has no profile picture.
     *
     * @values  boolean
     * @default false
     * @example
     * ```
     * %v-user{[value]: "demo.user", [forceImage]: "true"}
     * ```
     */
    public forceImage = input<boolean>(false);
    /**
     * Show a colorized dot next to the user's name
     *
     * @values  boolean
     * @default false
     * @example
     * ```
     * %v-user{[value]: "demo.user", [color]: "true"}
     * ```
     */
    public color = input<boolean>(false);
    public nameClass = input<string>();

    /** The sync resource of the user, only set if the user is provided as an ID. */
    private readonly resource = computed(() => {
        const value = this.value();
        return typeof value === "string" ? this.userService.get(value) : undefined;
    });

    protected readonly user = computed<SimpleUser>(() => {
        const value = this.value();
        if (typeof value !== "string") return value as SimpleUser;
        return this.resource().value();
    });

    /** True while the user is resolved from its ID. */
    protected readonly loading = computed(() => {
        const resource = this.resource();
        if (!resource) return false;
        this.user();
        return resource.isLoading();
    });

    protected readonly parsedValue = computed(() => formatUser(this.user(), this.format()));

    protected readonly userColor = computed(() => Globals.getHSL(this.user()?.userColor ?? this.user()?.defaultColor));

    protected readonly showAvatar = computed(() => this.forceImage() || !!this.image());

    private readonly resolvedImage = computed(() =>
        typeof this.value() === "string" ? (this.user() as SimpleUser & { image_ID?: string })?.image_ID : undefined
    );

    protected readonly avatarImage = computed(() => this.image() ?? this.resolvedImage());

    protected readonly nameSkeletonWidth = computed(() => {
        const format = this.format();
        if (format === "initial") return "2.5rem";
        if (format === "initialNoDot") return "1.5rem";
        return "8rem";
    });
}
